/**
 * The scene's background states in the Properties panel (spec 040 D-1): the
 * States cards of 035's `StatesSection` without the spritesheet branch, the
 * same add row and errors, and every edit one store action (one undo step,
 * written through 039's one writer). A background with no states shows the
 * add row and a hint.
 */
import React, { forwardRef, useState } from 'react';
import { useSceneStore, type BackgroundStateError } from '../../../stores/sceneStore';
import { useModalContext } from '../../../contexts/ModalContext';
import MediaPicker from '../../../modals/MediaPicker';
import StatesSection from '../../sheets/StatesSection';
import { BACKGROUND_STATE_NAME_MAX, backgroundDefaultState, backgroundStates } from '../../../../../Shared/src/utils/backgroundStates';
import styles from './PropertiesPanel.module.scss';

/** The inline message of a refused state edit; `owner` words the "last state" refusal. */
export function stateErrorText(error: BackgroundStateError | null, owner: 'sprite' | 'background'): string | null {
  switch (error) {
    case null: return null;
    case 'duplicate': return 'A state with that name already exists.';
    case 'empty': return 'A state needs a name.';
    case 'last': return owner === 'sprite' ? 'A sprite keeps at least one state.' : 'A background keeps at least one state.';
    case 'long': return `A state name is at most ${BACKGROUND_STATE_NAME_MAX} characters.`;
    default: return 'That state no longer exists.';
  }
}

export const NO_BACKGROUND_STATES_HINT = 'Add a state to change this background during the scene';

const BackgroundStatesSection = forwardRef<HTMLInputElement>((_props, addInputRef) => {
  const { addModal, removeModal } = useModalContext();
  const background = useSceneStore((s) => s.scene?.background);
  const addBackgroundState = useSceneStore((s) => s.addBackgroundState);
  const removeBackgroundState = useSceneStore((s) => s.removeBackgroundState);
  const renameBackgroundState = useSceneStore((s) => s.renameBackgroundState);
  const setDefaultBackgroundState = useSceneStore((s) => s.setDefaultBackgroundState);
  const assignBackgroundStateMedia = useSceneStore((s) => s.assignBackgroundStateMedia);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<BackgroundStateError | null>(null);

  const states = backgroundStates(background);

  const add = () => {
    const refusal = addBackgroundState(newName);
    setError(refusal);
    if (!refusal) setNewName('');
  };

  const pickMedia = (state: string) => {
    addModal({
      title: `Select Image for "${state}"`,
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={(mediaId: number) => {
            removeModal();
            setError(assignBackgroundStateMedia(state, mediaId));
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
    });
  };

  return (
    <div data-testid="background-states">
      {states.length > 0 && (
        <StatesSection
          states={states}
          defaultState={backgroundDefaultState(background)}
          onSetDefault={(name) => setError(setDefaultBackgroundState(name))}
          onDelete={(name) => setError(removeBackgroundState(name))}
          onPickMedia={pickMedia}
          onRename={(from, to) => stateErrorText(renameBackgroundState(from, to), 'background')}
        />
      )}
      {states.length === 0 && <p className={styles.fieldHint} data-testid="background-states-hint">{NO_BACKGROUND_STATES_HINT}</p>}
      <div className={styles.addState}>
        <input
          ref={addInputRef}
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New state name..."
          aria-label="New background state name"
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button onClick={add} disabled={!newName.trim()}>
          Add
        </button>
      </div>
      {error && (
        <span className={styles.fieldHint} role="alert">{stateErrorText(error, 'background')}</span>
      )}
    </div>
  );
});

BackgroundStatesSection.displayName = 'BackgroundStatesSection';

export default BackgroundStatesSection;
